import { RequestType } from '@/enums'
import i18n from '@/features/locales/i18n'
import {
  BadRequestError,
  ForbiddenError,
  NetworkError,
  ServerError,
  TooManyRequestsError,
  UnauthorisedError
} from '@/errors/index.js'
import { useLocaleStore } from '@/features/locales/stores/locale'
import { useAuthStore } from '@/pinia/auth'
import { useBrandedStore } from '@/pinia/branded'
import { config } from '@/utils/config'
import { captureEvent } from '@sentry/browser'
import DOMPurify from 'dompurify'

let refreshPromise = null

const MAX_RETRIES = 2
const RETRY_DELAY_MS = 500

export async function request({ method, requestType = RequestType.POST, payload = {}, signal, retryCount = 0 }) {
  const url = method.endpoint ? method.endpoint + method.method : config.api_url + method
  const path = method?.method || method
  const authStore = useAuthStore()
  const localeStore = useLocaleStore()

  // Build the request. Only fails if the url, headers or body are malformed, before anything is sent
  let httpRequest
  try {
    httpRequest = new Request(url, {
      method: requestType === RequestType.POST ? 'POST' : 'GET',
      credentials: 'include',
      headers: setHeaders(payload),
      signal,
      body: setBody(requestType, payload)
    })
  } catch (error) {
    return handleInvalidRequest(error, path)
  }

  if (shouldRefresh(authStore, method)) {
    const refresh = await refreshAuthCookie(localeStore.locale)
    if (refresh) {
      authStore.setSessionExpiry(refresh)
    }
  }

  // Send the request and read the response. The body streams in after the headers, so this can fail on
  // the connection during either phase, or on parsing once the body has arrived
  let response
  let body
  try {
    response = await fetch(httpRequest)
    body = response.ok ? await parseResponse(response) : await response.text()
  } catch (error) {
    if (error?.name === 'AbortError') return { cancelled: true }
    if (canRetry(error, requestType, retryCount)) {
      await delay(RETRY_DELAY_MS)
      return request({ method, requestType, payload, signal, retryCount: retryCount + 1 })
    }
    // No response means the fetch itself failed, otherwise it failed while reading the body
    return response ? handleBodyError(error, path) : handleNetworkError(error)
  }

  if (!response.ok) {
    return handleHttpError(payload, body, response.status, path)
  }
  return body
}

function canRetry(error, requestType, retryCount) {
  return retryCount < MAX_RETRIES && error instanceof TypeError && requestType === RequestType.GET
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function setHeaders(payload) {
  const localeStore = useLocaleStore()
  const headers = {
    'Content-Type': 'application/json',
    'Accept-Language': localeStore.locale,
    'X-Quotila-App': window.VITE_APP_INSTALLATION_TYPE
  }
  if (payload instanceof FormData) {
    delete headers['Content-Type']
  }
  return headers
}

function setBody(requestType, payload) {
  if (requestType === RequestType.GET) {
    return undefined
  }
  if (payload instanceof FormData) {
    return payload
  }
  return JSON.stringify(payload)
}

function canRefreshForMethod(method) {
  const normalizedMethod = `${method?.method || method || ''}`.toLowerCase()
  return !['auth/refresh', 'auth/getcookie', 'auth/getjwt'].includes(normalizedMethod)
}

function shouldRefresh(authStore, method) {
  if (!authStore.loggedIn || !authStore.sessionRefreshAfter || !canRefreshForMethod(method)) {
    return false
  }

  return new Date(authStore.sessionRefreshAfter).getTime() <= Date.now()
}

function refreshAuthCookie(locale) {
  if (refreshPromise) {
    return refreshPromise
  }

  // No signal: the refresh outlives any single caller's lifecycle.
  refreshPromise = fetch(`${config.api_url}auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Accept-Language': locale,
      'X-Quotila-App': window.VITE_APP_INSTALLATION_TYPE
    }
  })
    .then(async (refreshResponse) => (refreshResponse.ok ? refreshResponse.json() : false))
    .catch(() => false)
    .finally(() => {
      refreshPromise = null
    })

  return refreshPromise
}

async function parseResponse(response) {
  if (response.status === 204 || response.status === 205) {
    return null
  }

  const contentType = (response.headers.get('content-type') || '').toLowerCase()

  if (contentType.includes('application/json')) return response.json()

  if (isDownloadContent(contentType)) {
    const blob = await response.blob()
    const fileName = parseContentDispositionFileName(response.headers.get('content-disposition'))
    return new File([blob], fileName, { type: blob.type })
  }

  if (contentType.startsWith('text/') || contentType.includes('xml')) return response.text()

  return response.text()
}

function isDownloadContent(contentType) {
  return (
    contentType.includes('application/pdf') ||
    contentType.includes('application/octet-stream') ||
    contentType.startsWith('image/') ||
    contentType.includes('csv')
  )
}

function parseContentDispositionFileName(contentDisposition) {
  if (!contentDisposition) {
    return ''
  }
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition)
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1])
  }
  const asciiMatch = /filename="?([^";]+)"?/i.exec(contentDisposition)
  return asciiMatch?.[1] ?? ''
}

function handleInvalidRequest(error, path) {
  const eventId = captureEvent({
    message: `Invalid request : ${path}`,
    tags: { status: 0 },
    fingerprint: ['invalid-request', path],
    extra: {
      error: error?.message
    }
  })
  throw new ServerError(error?.message, eventId)
}

function handleNetworkError(error) {
  const eventId = captureEvent({
    message: `Status 0 - ${error?.message}`,
    tags: { status: 0 },
    fingerprint: ['network-error'],
    extra: {
      error: error?.message
    }
  })
  throw new NetworkError(error?.message, eventId)
}

// A TypeError here means the connection dropped mid-download, anything else means the body was unreadable
function handleBodyError(error, path) {
  if (error instanceof TypeError) {
    return handleNetworkError(error)
  }
  const eventId = captureEvent({
    message: `Invalid response body : ${path}`,
    tags: { status: 0 },
    fingerprint: ['invalid-response-body', path],
    extra: {
      error: error?.message
    }
  })
  throw new ServerError(error?.message, eventId)
}

function handleHttpError(payload, body, status, path) {
  if (status === 400) {
    handle400(payload, body, status, path)
  } else if (status === 401) {
    handle401(payload, body, status, path)
  } else if (status === 403) {
    handle403(payload, body, status, path)
  } else if (status === 429) {
    handle429(status)
  } else {
    handle500(payload, body, status, path)
  }
}

function handle400(payload, body, status, path) {
  const eventId = captureSentryEvent(payload, body, status, path)
  const popUp = extractResponseToPopUp(body)
  throw new BadRequestError(`Status ${status}`, eventId, popUp)
}

function handle401(payload, body, status, path) {
  const brandedStore = useBrandedStore()
  const authStore = useAuthStore()
  const redirectURL =
    authStore.installation === 'branded'
      ? brandedStore.launchURL
      : `/logout?${new URLSearchParams({ path: authStore.login_url, redirect: window.location.pathname + window.location.search })}`
  const expiry = authStore.sessionExpiry
  const expiryTime = new Date(expiry).getTime()

  // Log 401 if no expiry exists, if expiry is an invalid date or if expiry is in future
  if (!expiry || Number.isNaN(expiryTime) || expiryTime > Date.now()) {
    captureSentryEvent(payload, body, status, path)
  }
  window.logoutPush = true
  throw new UnauthorisedError(`Status ${status}`, redirectURL)
}

function handle403(payload, body, status, path) {
  const brandedStore = useBrandedStore()
  const authStore = useAuthStore()
  const redirectURL =
    authStore.installation === 'branded'
      ? brandedStore.launchURL
      : `/logout?${new URLSearchParams({ path: authStore.login_url, redirect: window.location.pathname + window.location.search })}`
  captureSentryEvent(payload, body, status, path)
  const popUp = extractResponseToPopUp(body)
  throw new ForbiddenError(`Status ${status}`, redirectURL, popUp)
}

function handle429(status) {
  throw new TooManyRequestsError(`Status ${status}`)
}

function handle500(payload, body, status, path) {
  const eventId = captureSentryEvent(payload, body, status, path)
  throw new ServerError(`Status ${status}`, eventId)
}

function captureSentryEvent(payload, body, status, path) {
  return captureEvent({
    message: `Status ${status} : ${path}`,
    fingerprint: ['api-error', status, path],
    tags: { status },
    extra: {
      payload: scrubPayload(payload),
      response: parseJSON(body),
      status
    }
  })
}

function parseJSON(response) {
  if (!response) return undefined
  try {
    return JSON.parse(response)
  } catch {
    return response
  }
}

function scrubPayload(payload) {
  if (!payload || typeof payload !== 'object') return undefined
  if (payload instanceof FormData) return { formData: [...payload.keys()] }
  const scrubbed = { ...payload }
  for (const key of Object.keys(scrubbed)) {
    if (/token|password|secret/i.test(key)) {
      delete scrubbed[key]
    }
  }
  return scrubbed
}

function extractResponseToPopUp(response) {
  let message = ''

  const data = parseJSON(response)
  if (!data) return { title: i18n.global.t('common.validation.validationErrorsOccurred') }

  if (typeof data === 'string') {
    return { title: DOMPurify.sanitize(data) }
  }

  Object.assign(data, Object.fromEntries(Object.entries(data ?? {}).map(([key, value]) => [key.toLowerCase(), value])))

  if (data.title) {
    message += `${data.title}<br>`
  }
  if (data.message) {
    message += `${data.message}<br>`
  }
  if (data.detail) {
    message += `${data.detail}<br>`
  }
  if (data.issues) {
    message += data.issues.join('<br>')
  }
  if (data.errors) {
    message += Object.values(data.errors).flat().join('<br>')
  }

  if (message) {
    return {
      title: DOMPurify.sanitize(message.split('<br>')[0]),
      html: DOMPurify.sanitize(message.split('<br>').slice(1).join('<br>'))
    }
  }

  return { title: i18n.global.t('common.validation.validationErrorsOccurred') }
}
